/**
 * QuickPasteAddress Component
 *
 * Collapsible paste area that parses free-form address text into structured fields.
 * Uses rule-based parsing first; falls back to Claude (AI) when city or postalCode
 * can't be extracted — e.g. for unfamiliar regional formats.
 *
 * Supported rule-based formats: Indian, US, UK, Australian.
 * AI fallback handles any other format automatically.
 */

import { useState } from 'react'

import {
  type ParseStatus,
  SpinnerIcon,
  StatusNotice,
  getParseButtonClass,
  getParseButtonLabel,
} from './QuickPasteAddress-parts'
import { parseAddressWithAI } from '../../services/address-ai-service'

export interface ParsedAddress {
  line1: string
  line2: string
  city: string
  state: string
  stateCode: string
  postalCode: string
  gstin?: string
}

export interface QuickPasteAddressProps {
  id: string
  onParse: (address: ParsedAddress) => void
  placeholder?: string
  includeGSTIN?: boolean
}

// =============================================================================
// State data
// =============================================================================

export const INDIAN_STATES: Record<string, string> = {
  'Kerala': '32', 'Karnataka': '29', 'Tamil Nadu': '33', 'Maharashtra': '27',
  'Gujarat': '24', 'Delhi': '07', 'Rajasthan': '08', 'Punjab': '03',
  'Haryana': '06', 'Uttar Pradesh': '09', 'Madhya Pradesh': '23',
  'West Bengal': '19', 'Bihar': '10', 'Odisha': '21', 'Andhra Pradesh': '37',
  'Telangana': '36', 'Assam': '18', 'Jharkhand': '20', 'Chhattisgarh': '22',
  'Uttarakhand': '05', 'Himachal Pradesh': '02', 'Jammu and Kashmir': '01',
  'Goa': '30', 'Sikkim': '11', 'Meghalaya': '17', 'Manipur': '14',
  'Mizoram': '15', 'Tripura': '16', 'Nagaland': '13', 'Arunachal Pradesh': '12',
}

export const AUSTRALIAN_STATES: Record<string, string> = {
  'NSW': 'NSW', 'VIC': 'VIC', 'QLD': 'QLD', 'WA': 'WA',
  'SA': 'SA', 'TAS': 'TAS', 'ACT': 'ACT', 'NT': 'NT',
  'New South Wales': 'NSW', 'Victoria': 'VIC', 'Queensland': 'QLD',
  'Western Australia': 'WA', 'South Australia': 'SA', 'Tasmania': 'TAS',
  'Australian Capital Territory': 'ACT', 'Northern Territory': 'NT',
}

export const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
])

// =============================================================================
// Address parsing helpers
// =============================================================================

interface ParseState {
  line1: string
  line2: string
  city: string
  state: string
  stateCode: string
  postalCode: string
}

export function extractSpecialFields(lines: string[]): { gstin: string; addressLines: string[] } {
  let gstin = ''
  const addressLines: string[] = []

  for (const line of lines) {
    if (line.match(/^GST(?:IN)?:/i)) {
      gstin = line.replace(/^GST(?:IN)?:\s*/i, '').trim()
    } else if (!line.match(/^Place of Supply:/i) && !line.match(/^Company Code:/i)) {
      addressLines.push(line)
    }
  }

  return { gstin, addressLines }
}

function tryIndianFormat(line: string, ps: ParseState): boolean {
  const match = line.match(/^(.+?)\s*-\s*(\d{6})$/)
  if (!match?.[1] || !match[2] || ps.postalCode) { return false }

  const parts = match[1].split(',').map(p => p.trim())
  if (parts.length > 1) {
    /* v8 ignore next 2 */
    ps.city = parts[parts.length - 1] ?? ''
    if (!ps.line1) { ps.line1 = parts.slice(0, -1).join(', ') }
  } else {
    ps.city = match[1]
  }
  ps.postalCode = match[2]
  return true
}

function tryUSFormat(line: string, ps: ParseState): boolean {
  const match = line.match(/^(.+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/)
  if (!match?.[1] || !match[2] || !match[3] || !US_STATES.has(match[2]) || ps.postalCode) { return false }

  ps.city = match[1]
  ps.stateCode = match[2]
  ps.state = match[2]
  ps.postalCode = match[3]
  return true
}

function tryAustralianFormat(line: string, ps: ParseState): boolean {
  const match = line.match(/^(.+?)\s+([A-Z]{2,3})\s+(\d{4})$/)
  if (!match?.[1] || !match[2] || !match[3] || !AUSTRALIAN_STATES[match[2]] || ps.postalCode) { return false }

  ps.city = match[1]
  /* v8 ignore next 2 */
  ps.stateCode = AUSTRALIAN_STATES[match[2]] ?? ''
  ps.state = AUSTRALIAN_STATES[match[2]] ?? ''
  ps.postalCode = match[3]
  return true
}

function applyUKLocationParts(locationParts: string[], ps: ParseState): void {
  if (locationParts.length >= 2) {
    /* v8 ignore next 2 */
    ps.city = locationParts[locationParts.length - 2] ?? ''
    ps.state = locationParts[locationParts.length - 1] ?? ''
    if (locationParts.length > 2) {
      ps.line1 = ps.line1 || locationParts.slice(0, -2).join(' ')
    }
    return
  }
  if (locationParts.length === 1) {
    /* v8 ignore next */
    ps.city = locationParts[0] ?? ''
  }
}

function tryUKFormatWithDash(line: string, ps: ParseState): boolean {
  const match = line.match(/^(.+?)\s*-\s*([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})/i)
  if (!match?.[1] || !match[2] || ps.postalCode) { return false }

  const locationParts = match[1].split(/\s+/).filter(Boolean)
  applyUKLocationParts(locationParts, ps)
  ps.postalCode = match[2].trim()
  return true
}

function tryUKFormatNoDash(line: string, ps: ParseState): boolean {
  const match = line.match(/^(.+?)\s+([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})$/i)
  if (!match?.[1] || !match[2] || ps.postalCode) { return false }

  ps.city = match[1]
  ps.postalCode = match[2].trim()
  return true
}

function tryStateLine(line: string, ps: ParseState): boolean {
  if (!line || ps.state) { return false }

  if (INDIAN_STATES[line]) {
    ps.state = line
    ps.stateCode = INDIAN_STATES[line]
    return true
  }
  if (AUSTRALIAN_STATES[line]) {
    ps.state = line
    ps.stateCode = AUSTRALIAN_STATES[line]
    return true
  }
  return false
}

function assignRegularLine(line: string, ps: ParseState): void {
  if (!line) { return }
  if (!ps.line1) {
    ps.line1 = line
  } else if (
    !ps.line2 &&
    !line.includes('India') && !line.includes('Australia') &&
    !line.includes('United States') && !line.includes('United Kingdom') &&
    !line.includes(ps.city)
  ) {
    ps.line2 = line
  }
}

export function parseAddressText(text: string, includeGSTIN: boolean): ParsedAddress {
  const rawLines = text.split('\n').map(l => l.trim()).filter(l => l)
  const { gstin, addressLines } = extractSpecialFields(rawLines)

  const ps: ParseState = { line1: '', line2: '', city: '', state: '', stateCode: '', postalCode: '' }

  for (const line of addressLines) {
    if (tryIndianFormat(line, ps)) { continue }
    if (tryUSFormat(line, ps)) { continue }
    if (tryAustralianFormat(line, ps)) { continue }
    if (tryUKFormatWithDash(line, ps)) { continue }
    if (tryUKFormatNoDash(line, ps)) { continue }
    if (tryStateLine(line, ps)) { continue }
    assignRegularLine(line, ps)
  }

  return {
    line1: ps.line1,
    line2: ps.line2,
    city: ps.city,
    state: ps.state,
    stateCode: ps.stateCode,
    postalCode: ps.postalCode,
    ...(includeGSTIN && gstin ? { gstin } : {}),
  }
}

/** Returns true when the rule parser couldn't extract key fields — AI fallback needed. */
function needsAI(parsed: ParsedAddress): boolean {
  return !parsed.city && !parsed.postalCode
}

// =============================================================================
// Default placeholder
// =============================================================================

export function getDefaultPlaceholder(includeGSTIN: boolean): string {
  const example = includeGSTIN
    ? 'e.g. VJ Towers, Vyttilla, Cochin - 682019, Kerala, GSTIN: 32AAACB2100P1Z6'
    : 'e.g. VJ Towers, Vyttilla, Cochin - 682019, Kerala'
  return `Paste a full address in any format — we'll fill the fields below.\n${example}`
}

// =============================================================================
// Component
// =============================================================================

interface ParseDriverArgs {
  includeGSTIN: boolean
  onParse: (a: ParsedAddress) => void
  setStatus: (s: ParseStatus) => void
  setDidUseAI: (v: boolean) => void
  setIsVisible: (v: boolean) => void
  getTextarea: () => HTMLTextAreaElement | null
}

function clearTextarea(getTextarea: () => HTMLTextAreaElement | null): void {
  const ta = getTextarea()
  if (ta) { ta.value = '' }
}

async function runParseFlow(text: string, args: ParseDriverArgs): Promise<void> {
  const { onParse, setStatus, setDidUseAI, setIsVisible, getTextarea } = args
  setStatus('parsing')
  setDidUseAI(false)

  const local = parseAddressText(text, args.includeGSTIN)
  if (!needsAI(local)) {
    onParse(local)
    setStatus('done')
    clearTextarea(getTextarea)
    setTimeout(() => { setIsVisible(false); setStatus('idle') }, 600)
    return
  }
  setStatus('ai-parsing')
  try {
    const ai = await parseAddressWithAI(text, args.includeGSTIN)
    onParse(ai)
    setDidUseAI(true)
    setStatus('done')
    clearTextarea(getTextarea)
    setTimeout(() => { setIsVisible(false); setStatus('idle'); setDidUseAI(false) }, 1200)
  } catch {
    onParse(local)
    setStatus('error')
    setTimeout(() => { setStatus('idle') }, 2000)
  }
}

interface ParseControlsProps {
  status: ParseStatus
  didUseAI: boolean
  isBusy: boolean
  onParse: () => void
  onClear: () => void
}

function ParseControls({ status, didUseAI, isBusy, onParse, onClear }: ParseControlsProps): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <button className={getParseButtonClass(status, didUseAI)} disabled={isBusy} type="button" onClick={onParse}>
        {isBusy && <SpinnerIcon />}
        {getParseButtonLabel(status, didUseAI)}
      </button>
      <button
        className="px-3 py-1 text-xs font-medium text-foreground-secondary hover:text-foreground bg-surface-secondary hover:bg-muted rounded-md transition-colors"
        disabled={isBusy}
        type="button"
        onClick={onClear}
      >
        Clear
      </button>
    </div>
  )
}

function ClipboardIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ToggleHeader({ isVisible, didUseAI, onToggle }: { isVisible: boolean; didUseAI: boolean; onToggle: () => void }): JSX.Element {
  return (
    <button
      className="flex items-center el-gap-xs text-sm text-foreground-secondary hover:text-foreground font-medium transition-colors"
      type="button"
      onClick={onToggle}
    >
      <ClipboardIcon />
      <span>Quick paste address</span>
      {didUseAI && <span className="text-xs text-violet-600 dark:text-violet-400 font-normal">· AI</span>}
      <svg className={`w-3.5 h-3.5 transition-transform ${isVisible ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

interface QuickPasteAddressHandlers {
  runParse: (text: string) => Promise<void>
  handleParse: () => void
  handleClear: () => void
  handleBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  handleToggle: () => void
}

function useQuickPasteHandlers(
  args: ParseDriverArgs & { status: ParseStatus; isVisible: boolean },
): QuickPasteAddressHandlers {
  const { status, isVisible, setIsVisible, setStatus, getTextarea } = args
  const runParse = (text: string): Promise<void> => runParseFlow(text, args)
  return {
    runParse,
    handleParse: () => {
      const text = getTextarea()?.value.trim() ?? ''
      if (text) { void runParse(text) }
    },
    handleClear: () => { clearTextarea(getTextarea); setIsVisible(false); setStatus('idle') },
    handleBlur: (e) => {
      const text = e.currentTarget.value.trim()
      if (text && status === 'idle') { void runParse(text) }
    },
    handleToggle: () => {
      const isNextVisible = !isVisible
      setIsVisible(isNextVisible)
      if (isNextVisible) { setTimeout(() => { getTextarea()?.focus() }, 100) }
    },
  }
}

export function QuickPasteAddress({
  id,
  onParse,
  placeholder,
  includeGSTIN = false,
}: QuickPasteAddressProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false)
  const [status, setStatus] = useState<ParseStatus>('idle')
  const [didUseAI, setDidUseAI] = useState(false)
  const getTextarea = (): HTMLTextAreaElement | null =>
    document.getElementById(id) as HTMLTextAreaElement | null

  const { handleParse, handleClear, handleBlur, handleToggle } = useQuickPasteHandlers({
    includeGSTIN, onParse, setStatus, setDidUseAI, setIsVisible, getTextarea, status, isVisible,
  })
  const isBusy = status === 'parsing' || status === 'ai-parsing'

  return (
    <div className="el-field-container el-space-y-xs">
      <div className="flex items-center justify-between">
        <ToggleHeader didUseAI={didUseAI} isVisible={isVisible} onToggle={handleToggle} />
        {isVisible && (
          <ParseControls didUseAI={didUseAI} isBusy={isBusy} status={status} onClear={handleClear} onParse={handleParse} />
        )}
      </div>
      {isVisible && (
        <div>
          <textarea
            className="block w-full px-3 py-2 rounded-md border border-border bg-card text-foreground text-sm resize-y focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring placeholder:text-muted-foreground"
            id={id}
            placeholder={placeholder ?? getDefaultPlaceholder(includeGSTIN)}
            rows={3}
            onBlur={handleBlur}
          />
          <StatusNotice status={status} />
        </div>
      )}
    </div>
  )
}
